import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, RefreshCw, ArrowLeft, Settings, User, LogOut, Key, UserCircle, Sun, Moon, BarChart3, TrendingUp, Edit3, PlusCircle, Check, X, ChevronLeft, ChevronRight, Download, Printer, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, BarChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import API from '../Api';
import { useLogout } from '../hooks/useLogout';
import { useAuth } from '../context/AuthContext';
import './WidgetRenderer.css';
import './LogExplorer.css';
import UserTimezoneSelect from './UserTimezoneSelect';
import AskCommrayAi from './AskCommrayAi';
import { draftSearchFilters } from './logExplorerDraft';
import LogQueryEditor from './LogQueryEditor';
import LogSourceTree from './logSourceTree';
import LogResultList from './LogResultList';
import { filterLogsForSelectedIndex, resolveSourcesForSelection, sourceIdentityKeys } from './logSourceFilters';
import {
  formatTimestampWithMillisUser,
  formatTimelineBucketLabelUser,
  formatDatetimeLocalForUser,
  parseLogTimestampToDate,
  parseLogTimestampToMillis,
  parseDatetimeLocalFilterToDate,
  parseDatetimeLocalFilterToMillis,
  formatFilterTimeForApi,
  getDateTimePartsInUserTz,
  getUserTimezone
} from '../utils/userTimezone';
import { useUserTimezone } from '../hooks/useUserTimezone';
import {
  logsToCsvContent,
  logsToPlainTextContent,
  slugifyFilenamePart,
  saveTextFileWithPicker,
  SAVE_PICKER_TYPES_CSV,
  SAVE_PICKER_TYPES_TXT
} from '../utils/logExport';

const LOG_SOURCES_STORAGE_KEY_BASE = 'logExplorerSources';
const LOG_EXPLORER_FILTERS_KEY_BASE = 'logExplorerSearchFilters';

function currentUsername() {
  try {
    return (localStorage.getItem('username') || 'anonymous').trim() || 'anonymous';
  } catch {
    return 'anonymous';
  }
}

const LOG_SOURCE_PLATFORMS = [
  ['GCP', 'GCP'],
  ['AWS', 'AWS'],
  ['AZURE', 'Azure'],
  ['KUBERNETES', 'Kubernetes'],
  ['OPENSHIFT', 'OpenShift'],
  ['DOCKER', 'Docker'],
  ['ON_PREM', 'On-Premises'],
  ['OTHER', 'Other']
];

const LOG_SOURCE_COLLECTION_METHODS = [
  ['COMMRAY_AGENT', 'Commray Agent'],
  ['OPEN_TELEMETRY', 'OpenTelemetry'],
  ['HTTP_API', 'HTTP API'],
  ['SYSLOG', 'Syslog'],
  ['CLOUDWATCH', 'CloudWatch'],
  ['GCP_LOGGING', 'GCP Logging'],
  ['AZURE_MONITOR', 'Azure Monitor'],
  ['FLUENT_BIT', 'Fluent Bit'],
  ['FLUENTD', 'Fluentd'],
  ['OTHER', 'Other']
];

const LOG_SOURCE_ENVIRONMENTS = [
  ['DEVELOPMENT', 'Development'],
  ['TEST', 'Test'],
  ['STAGING', 'Staging'],
  ['PRODUCTION', 'Production'],
  ['OTHER', 'Other']
];

const LOG_SOURCE_PLATFORM_VALUES = new Set(LOG_SOURCE_PLATFORMS.map(([value]) => value));
const LOG_SOURCE_COLLECTION_VALUES = new Set(LOG_SOURCE_COLLECTION_METHODS.map(([value]) => value));
const LOG_SOURCE_ENVIRONMENT_VALUES = new Set(LOG_SOURCE_ENVIRONMENTS.map(([value]) => value));

function logSourceLabel(options, value) {
  const match = options.find(([option]) => option === value);
  return match ? match[1] : (value || '');
}

function tagsToText(tags) {
  if (!tags || typeof tags !== 'object') return '';
  return Object.entries(tags)
    .filter(([key]) => String(key || '').trim())
    .map(([key, value]) => `${key}=${value ?? ''}`)
    .join('\n');
}

function parseTagsText(text) {
  const tags = {};
  const lines = String(text || '').split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) {
      return { error: `Tag "${trimmed}" must look like key=value` };
    }
    const key = trimmed.slice(0, eq).trim();
    const value = trimmed.slice(eq + 1).trim();
    if (!/^[A-Za-z0-9_.:-]{1,64}$/.test(key)) {
      return { error: `Tag key "${key}" must use letters, numbers, ., _, :, or -` };
    }
    tags[key] = value;
  }
  if (Object.keys(tags).length > 50) {
    return { error: 'A source can have at most 50 tags' };
  }
  return { tags };
}

function logSourcesStorageKey() {
  return `${LOG_SOURCES_STORAGE_KEY_BASE}:${currentUsername()}`;
}

function logExplorerFiltersStorageKey() {
  return `${LOG_EXPLORER_FILTERS_KEY_BASE}:${currentUsername()}`;
}

function isCommrayLogsEndpoint(endpoint) {
  if (!endpoint || typeof endpoint !== 'string') return false;
  const value = endpoint.toLowerCase();
  if (
    value.includes('market-service') ||
    value.includes('/api/stocks/') ||
    value.includes('/api/mock/') ||
    value.includes('/api/market/')
  ) {
    return false;
  }
  return (
    value.includes('/api/monitoring/logs') ||
    value.includes('/api/logs/query') ||
    /\/api\/logs(\?|$|#)/.test(value)
  );
}

/** Label the user gave this source (application, otherwise index). */
function sourceServiceLabel(source) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const apps = Array.isArray(source.applications) ? source.applications : [];
  return String(apps[0] || source.applicationName || source.indexName || '').trim();
}

/** Keyword search: message / traceId / userId only (level has its own filter). */
function logMatchesKeyword(log, keyword) {
  const kw = String(keyword || '').trim().toLowerCase();
  if (!kw) return true;
  const message = String(log.message || '').toLowerCase();
  const traceId = String(log.traceId || log.trace_id || '').toLowerCase();
  const userId = String(log.userId || log.user_id || '').toLowerCase();
  return message.includes(kw) || traceId.includes(kw) || userId.includes(kw);
}

/**
 * Detect LogQL only for clear field/operator queries.
 * Avoid treating Trace IDs (Root=...) or plain names as LogQL.
 */
function looksLikeLogQL(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  if (/\b(level|service|message|timestamp|traceId|trace_id|userId|user_id)\s*(=|!=|>=|<=|>|<|CONTAINS|LIKE|IN|REGEX)\b/i.test(trimmed)) {
    return true;
  }
  if (/\b(CONTAINS|LIKE|BETWEEN|REGEX)\b/i.test(trimmed)) return true;
  if (/\b(AND|OR)\b/i.test(trimmed) && /\b(level|service|message|timestamp|traceId|trace_id)\b/i.test(trimmed)) {
    return true;
  }
  if (/^\s*\w+\s+(=|!=|>=|<=|>|<)\s+/.test(trimmed)) return true;
  if (/^\s*\w+\s*(=|!=|>=|<=|>|<)\s*["']/.test(trimmed)) return true;
  return false;
}

/** Sources matching index and optional application filter from Log Explorer UI. */
function deriveDefaultApplicationFromEndpoint(endpoint) {
  try {
    const url = new URL(String(endpoint || '').trim());
    const slug = url.pathname
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
    return slug || url.hostname.replace(/[^a-zA-Z0-9]+/g, '-');
  } catch {
    return 'app';
  }
}

function deriveDefaultIndexFromEndpoint(endpoint) {
  try {
    const url = new URL(String(endpoint || '').trim());
    return url.hostname.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'external-log-source';
  } catch {
    return String(endpoint || '')
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'external-log-source';
  }
}

/** Keep the index and application names the user entered. Fill blanks from the URL. */
function migrateLogSourcesModel(sources) {
  if (!Array.isArray(sources) || sources.length === 0) {
    return sources;
  }
  return sources.map((source) => {
    const endpoint = source.endpoint || '';
    let indexName = String(source.indexName || '').trim();
    let applications = Array.isArray(source.applications)
      ? source.applications.map((app) => String(app).trim()).filter(Boolean)
      : [];
    if (!indexName) {
      indexName = deriveDefaultIndexFromEndpoint(endpoint);
    }
    if (applications.length === 0 && endpoint) {
      applications = [deriveDefaultApplicationFromEndpoint(endpoint)];
    }
    return { ...source, indexName, applications };
  });
}

/** Per-request SQL page. Sequential pages keep the DB pool free for login. */
const LOG_SEARCH_PAGE_SIZE = 500;
/** Safety cap only — fetch every match in range/index/app up to this. */
const LOG_SEARCH_MAX_ROWS = 100000;

function logIdentityKey(log) {
  if (log?.id != null && String(log.id).trim() !== '') {
    return `id:${log.id}`;
  }
  return `fallback:${log?.timestamp || ''}|${log?.service || ''}|${log?.traceId || ''}|${String(log?.message || '').slice(0, 80)}`;
}

const TIME_PRESETS = [
  { id: '5m', label: 'Last 5 minutes', ms: 5 * 60 * 1000 },
  { id: '15m', label: 'Last 15 minutes', ms: 15 * 60 * 1000 },
  { id: '60m', label: 'Last 60 minutes', ms: 60 * 60 * 1000 },
  { id: '4h', label: 'Last 4 hours', ms: 4 * 60 * 60 * 1000 },
  { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'today', label: 'Today (until now)' }
];

const QUICK_AMOUNTS = Array.from({ length: 60 }, (_, i) => i + 1);

const QUICK_UNITS = [
  { id: 'seconds', label: 'seconds', ms: 1000 },
  { id: 'minutes', label: 'minutes', ms: 60 * 1000 },
  { id: 'hours', label: 'hours', ms: 60 * 60 * 1000 },
  { id: 'days', label: 'days', ms: 24 * 60 * 60 * 1000 }
];

const REFRESH_UNITS = QUICK_UNITS.filter((unit) => unit.id !== 'days');

function pad2(n) {
  return String(n).padStart(2, '0');
}

function shiftCalendarDate(year, month, day, deltaDays) {
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Resolve preset / quick-select windows in the user's timezone. Absolute mode keeps the typed start/end. */
function resolveLogExplorerTimeWindow(filters, appTimezone) {
  const now = new Date();
  const endNow = formatDatetimeLocalForUser(now, appTimezone);
  const mode = filters?.timeMode || 'absolute';
  if (mode === 'absolute') {
    return { startTime: filters?.startTime || '', endTime: filters?.endTime || '' };
  }
  if (mode === 'quick') {
    const amount = Math.min(60, Math.max(1, Number(filters?.quickAmount) || 15));
    const unit = QUICK_UNITS.find((item) => item.id === filters?.quickUnit) || QUICK_UNITS[1];
    return {
      startTime: formatDatetimeLocalForUser(new Date(now.getTime() - amount * unit.ms), appTimezone),
      endTime: endNow
    };
  }
  const preset = TIME_PRESETS.find((item) => item.id === (filters?.timePreset || '15m')) || TIME_PRESETS[1];
  if (preset.ms) {
    return {
      startTime: formatDatetimeLocalForUser(new Date(now.getTime() - preset.ms), appTimezone),
      endTime: endNow
    };
  }
  const parts = getDateTimePartsInUserTz(now, appTimezone);
  if (!parts) {
    return { startTime: '', endTime: endNow };
  }
  const today = `${parts.y}-${pad2(parts.mo)}-${pad2(parts.da)}`;
  if (preset.id === 'today') {
    return { startTime: `${today}T00:00:00.000`, endTime: endNow };
  }
  const yesterday = shiftCalendarDate(parts.y, parts.mo, parts.da, -1);
  return { startTime: `${yesterday}T00:00:00.000`, endTime: `${yesterday}T23:59:59.999` };
}

const DEFAULT_LOG_EXPLORER_FILTERS = {
  selectedIndex: '',
  selectedApplication: '',
  service: '',
  level: '',
  keyword: '',
  logql: '',
  timeMode: 'absolute',
  timePreset: '15m',
  quickAmount: 15,
  quickUnit: 'minutes',
  refreshAmount: '',
  refreshUnit: 'minutes',
  startTime: '',
  endTime: '',
  page: 0,
  size: 10000
};

const readStoredLogExplorerFilters = () => {
  try {
    const raw = localStorage.getItem(logExplorerFiltersStorageKey());
    if (!raw) return { ...DEFAULT_LOG_EXPLORER_FILTERS };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_LOG_EXPLORER_FILTERS };
    return { ...DEFAULT_LOG_EXPLORER_FILTERS, ...parsed };
  } catch {
    return { ...DEFAULT_LOG_EXPLORER_FILTERS };
  }
};

const persistLogExplorerFilters = (next) => {
  try {
    localStorage.setItem(logExplorerFiltersStorageKey(), JSON.stringify(next));
  } catch (e) {
    console.warn('Failed to persist log explorer filters', e);
  }
};

// Empty by default — users add sources in Log Explorer.
const DEFAULT_LOG_SOURCES = [];

/** Timeline bucket size (ms) from selected search timeframe duration. */
const getTimelineIntervalMs = (timeRangeMs) => {
  if (!timeRangeMs || timeRangeMs <= 0) return 60 * 1000;
  if (timeRangeMs <= 1000) return 10;
  if (timeRangeMs <= 10 * 1000) return 100;
  if (timeRangeMs <= 5 * 60 * 1000) return 1000;
  if (timeRangeMs <= 5 * 60 * 60 * 1000) return 60 * 1000;
  if (timeRangeMs <= 6 * 24 * 60 * 60 * 1000) return 60 * 60 * 1000;
  return 24 * 60 * 60 * 1000;
};

const formatTimelineColumnNotice = (intervalMs) => {
  if (intervalMs < 1000) return `${intervalMs} ms per column`;
  if (intervalMs === 1000) return '1 second per column';
  if (intervalMs === 60 * 1000) return '1 minute per column';
  if (intervalMs === 60 * 60 * 1000) return '1 hour per column';
  if (intervalMs === 24 * 60 * 60 * 1000) return '1 day per column';
  const minutes = intervalMs / (60 * 1000);
  return `${minutes} minutes per column`;
};

const TIMELINE_AXIS_SECTIONS = 6;

const filterLogsByTimelineBucket = (logs, selection) => {
  if (!selection || !Array.isArray(logs)) return logs;
  const { bucketStartMs, intervalMs } = selection;
  if (bucketStartMs == null || !intervalMs) return logs;
  const endMs = bucketStartMs + intervalMs;
  return logs.filter((log) => {
    const ts = log.timestamp || log.time || log.createdAt || log.date;
    const t = parseLogTimestampToMillis(ts);
    if (t == null) return false;
    return t >= bucketStartMs && t < endMs;
  });
};

/** Recharts 3 Bar passes the rectangle item (spread data row + payload); normalize to chart row. */
const timelinePayloadFromBarClick = (barData, index, timelineData) => {
  if (barData && barData.timestamp != null && barData.intervalMs != null) return barData;
  if (barData?.payload?.timestamp != null && barData.payload.intervalMs != null) return barData.payload;
  const nested = barData?.payload?.payload;
  if (nested?.timestamp != null && nested.intervalMs != null) return nested;
  const idx = Number.isFinite(index) ? index : Number(barData?.index);
  if (Array.isArray(timelineData) && Number.isFinite(idx) && timelineData[idx]?.timestamp != null) {
    return timelineData[idx];
  }
  return null;
};

/** Recharts + React recurse the fiber tree; tens of thousands of bars overflows the stack. */
const MAX_TIMELINE_BUCKETS = 500;

const countTimelineBuckets = (alignedStart, alignedEnd, intervalMs) => {
  if (!intervalMs || intervalMs <= 0) return 0;
  return Math.floor((alignedEnd - alignedStart) / intervalMs) + 1;
};

/**
 * Seven timestamps at the boundaries of six equal spans along the column axis:
 * first column, then every 1/6 of the way to the last column, then last column (k = 0..6).
 */
const buildTimelineSectionTickIndices = (n) => {
  if (n <= 0) return new Set();
  const last = n - 1;
  const set = new Set();
  if (last === 0) {
    set.add(0);
    return set;
  }
  for (let k = 0; k <= TIMELINE_AXIS_SECTIONS; k++) {
    const idx = Math.round((k * last) / TIMELINE_AXIS_SECTIONS);
    set.add(Math.min(Math.max(0, idx), last));
  }
  return set;
};

/** Thinner bars + tighter gaps when there are many columns; thicker when few. Assumes ~full-width chart. */
const getTimelineBarLayout = (n) => {
  const count = Math.max(n, 1);
  const maxBarSize = Math.max(2, Math.min(56, Math.round(680 / count)));
  let barCategoryGap;
  if (count > 150) barCategoryGap = '0%';
  else if (count > 80) barCategoryGap = '1%';
  else if (count > 40) barCategoryGap = '2%';
  else if (count > 20) barCategoryGap = '4%';
  else if (count > 10) barCategoryGap = '6%';
  else barCategoryGap = '10%';
  return { maxBarSize, barCategoryGap };
};

function LogTimelineXAxisTick({ x, y, payload, index, visibleIndices }) {
  if (!visibleIndices?.has(index)) return null;
  return (
    <text
      x={x}
      y={y}
      dy={16}
      textAnchor="end"
      className="log-timeline-axis-tick"
      fontSize={10}
      transform={`rotate(-45 ${x} ${y})`}
    >
      {payload?.value ?? ''}
    </text>
  );
}

const normalizeStoredSource = (rawSource) => {
  if (!rawSource || typeof rawSource !== 'object') {
    return null;
  }

  let endpoint = (rawSource.endpoint || '').trim();
  if (!endpoint) {
    return null;
  }

  // Collapse a duplicated scheme ("http://https://host") but keep a single https://.
  // Rewriting https to http makes Java stop on the redirect and store an empty body.
  endpoint = endpoint.replace(/^(https?:\/\/)(?=https?:\/\/)/i, '');
  if (!/^https?:\/\//i.test(endpoint)) {
    endpoint = endpoint.replace(/^\/+/, '');
    endpoint = 'http://' + endpoint;
  }
  // Remove trailing slashes
  endpoint = endpoint.replace(/\/+$/, '');
  // Replace multiple slashes with single (but preserve http://)
  endpoint = endpoint.replace(/([^:]\/)\/+/g, '$1');

  const applicationsRaw = Array.isArray(rawSource.applications)
    ? rawSource.applications
    : typeof rawSource.applications === 'string'
      ? rawSource.applications.split(',').map((app) => app.trim()).filter(Boolean)
      : [];

  const userIndex = String(rawSource.indexName || '').trim();
  const resolvedIndexName = userIndex || deriveDefaultIndexFromEndpoint(endpoint);
  const applications =
    applicationsRaw.length > 0 ? applicationsRaw : [deriveDefaultApplicationFromEndpoint(endpoint)];
  const applicationName = String(rawSource.applicationName || applications[0] || '').trim();
  const platform = LOG_SOURCE_PLATFORM_VALUES.has(rawSource.platform) ? rawSource.platform : 'OTHER';
  const collectionMethod = LOG_SOURCE_COLLECTION_VALUES.has(rawSource.collectionMethod)
    ? rawSource.collectionMethod
    : 'HTTP_API';
  const environment = LOG_SOURCE_ENVIRONMENT_VALUES.has(rawSource.environment)
    ? rawSource.environment
    : 'OTHER';
  const tags = rawSource.tags && typeof rawSource.tags === 'object' && !Array.isArray(rawSource.tags)
    ? rawSource.tags
    : {};

  return {
    id: rawSource.id || `source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    indexName: resolvedIndexName,
    applicationName,
    applications: applicationName && !applications.includes(applicationName)
      ? [applicationName, ...applications]
      : applications,
    environment,
    platform,
    collectionMethod,
    endpoint,
    collector: String(rawSource.collector || '').trim(),
    serviceName: String(rawSource.serviceName || rawSource.service || '').trim(),
    tags
  };
};

const fetchStoredLogSources = () => {
  try {
    const stored = localStorage.getItem(logSourcesStorageKey());
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return null;
    }

    const normalized = parsed
      .map((source) => normalizeStoredSource(source))
      .filter(Boolean);

    return normalized.length > 0 ? migrateLogSourcesModel(normalized) : null;
  } catch (error) {
    console.error('Failed to parse stored log sources', error);
    return null;
  }
};

const persistLogSources = (sources) => {
  try {
    if (!Array.isArray(sources)) {
      return;
    }

    localStorage.setItem(logSourcesStorageKey(), JSON.stringify(sources));
  } catch (error) {
    console.error('Failed to persist log sources', error);
  }
};

/**
 * Normalize time filter values for datetime-local inputs (step 0.001).
 * Values are wall-clock in the user's selected timezone (see Settings).
 */
const normalizeDatetimeLocalFilterValue = (v) => {
  if (v == null) return '';
  const raw = String(v).trim();
  if (!raw) return '';

  if (/Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const formatted = formatDatetimeLocalForUser(raw, getUserTimezone());
    if (formatted) return formatted;
    const d = parseLogTimestampToDate(raw);
    if (!d) return raw;
    const p2 = (n) => String(n).padStart(2, '0');
    const p3 = (n) => String(n).padStart(3, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}.${p3(d.getMilliseconds())}`;
  }

  const idx = raw.indexOf('T');
  if (idx === -1) return raw;
  const datePart = raw.slice(0, idx);
  const timePart = raw.slice(idx + 1);
  const parts = timePart.split(':');
  if (parts.length === 2) {
    return `${datePart}T${parts[0]}:${parts[1]}:00.000`;
  }
  if (parts.length >= 3) {
    const h = parts[0];
    const min = parts[1];
    let secPart = parts.slice(2).join(':');
    if (secPart.includes('.')) {
      const [sec, frac = ''] = secPart.split('.');
      const secNum = parseInt(sec, 10);
      const secPadded = Number.isNaN(secNum) ? sec.padStart(2, '0') : String(secNum).padStart(2, '0');
      const ms = (frac + '000').slice(0, 3);
      return `${datePart}T${h}:${min}:${secPadded}.${ms}`;
    }
    const secNum = parseInt(secPart, 10);
    const secPadded = Number.isNaN(secNum) ? String(secPart).padStart(2, '0') : String(secNum).padStart(2, '0');
    return `${datePart}T${h}:${min}:${secPadded}.000`;
  }
  return raw;
};

const logMatchesTimeFilters = (timestamp, startTime, endTime, appTimezone) => {
  const logDate = parseLogTimestampToDate(timestamp);
  if (!logDate) return false;
  if (startTime) {
    const startDate = parseDatetimeLocalFilterToDate(startTime, appTimezone);
    if (startDate && logDate < startDate) return false;
  }
  if (endTime) {
    const endDate = parseDatetimeLocalFilterToDate(endTime, appTimezone);
    if (!endDate || logDate.getTime() > endDate.getTime()) return false;
  }
  return true;
};

const LogExplorer = () => {
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]); // Store all logs for pagination
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchMatchTotal, setSearchMatchTotal] = useState(null);
  const [dbLogCount, setDbLogCount] = useState(null); // Track database log count
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchDescription, setSaveSearchDescription] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logExportMenuOpen, setLogExportMenuOpen] = useState(false);
  const logExportMenuRef = useRef(null);
  
  // New state for header functionality
  const [userInfo, setUserInfo] = useState({});
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const { planName } = useAuth();
  const isFreePlan = String(planName || 'free').toLowerCase() === 'free';
  
  const [filters, setFilters] = useState(readStoredLogExplorerFilters);
  
  // Chart type state for timeline visualization
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'
  /** When set, log table shows only events in that timeline column (same bucket as the bar). */
  const [timelineBucketSelection, setTimelineBucketSelection] = useState(null);

  // Initialize log sources: prioritize stored sources, only add defaults if not present
  const [logSources, setLogSources] = useState(() => {
    const stored = fetchStoredLogSources();

    const filteredStored = Array.isArray(stored) ? stored : [];

    if (filteredStored.length > 0) {
      console.log('Using stored log sources:', filteredStored.map((s) => `${s.indexName} (${s.endpoint})`));
      const updatedSources = migrateLogSourcesModel(filteredStored);
      persistLogSources(updatedSources);
      return updatedSources;
    }

    console.log('Using default log sources:', DEFAULT_LOG_SOURCES.map((s) => `${s.indexName} (${s.endpoint})`));
    return DEFAULT_LOG_SOURCES;
  });
  const [editingSources, setEditingSources] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sourcesError, setSourcesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await API.get('/api/monitoring/log-sources');
        if (cancelled) return;
        const remote = Array.isArray(response.data)
          ? response.data.map((source) => normalizeStoredSource(source)).filter(Boolean)
          : [];
        if (remote.length > 0) {
          const migrated = migrateLogSourcesModel(remote);
          persistLogSources(migrated);
          setLogSources(migrated);
          return;
        }
        const local = fetchStoredLogSources();
        if (local && local.length > 0) {
          await API.put('/api/monitoring/log-sources', { sources: local });
        }
      } catch (error) {
        console.warn('Using browser-saved log sources', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { timezone: appTimezone } = useUserTimezone();

  const visibleLogsForTable = useMemo(
    () => filterLogsByTimelineBucket(allLogs, timelineBucketSelection),
    [allLogs, timelineBucketSelection]
  );

  const logResultsPagingLabel = useMemo(() => {
    const loaded = visibleLogsForTable.length;
    if (loaded === 0) return null;
    const listPageSize = filters.size >= 10000 ? loaded : filters.size;
    const from = (currentPage - 1) * listPageSize + 1;
    const to = Math.min(currentPage * listPageSize, loaded);
    if (searchMatchTotal && searchMatchTotal > loaded) {
      return `Showing ${from.toLocaleString()} to ${to.toLocaleString()} of ${loaded.toLocaleString()} loaded (${searchMatchTotal.toLocaleString()} match in range)`;
    }
    return `Showing ${from.toLocaleString()} to ${to.toLocaleString()} of ${loaded.toLocaleString()} entries`;
  }, [visibleLogsForTable.length, filters.size, currentPage, searchMatchTotal]);

  useEffect(() => {
    fetchSavedSearches();
    fetchUserInfo();
    applyDarkMode();
    
  }, []);

  useEffect(() => {
    applyDarkMode();
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, [darkMode]);

  useEffect(() => {
    if (!filters.selectedIndex || !logSources?.length) {
      return;
    }
    const indexExists = logSources.some((source) => source.indexName === filters.selectedIndex);
    if (!indexExists) {
      setFilters((prev) => ({
        ...prev,
        selectedIndex: '',
        selectedApplication: ''
      }));
    }
  }, [logSources, filters.selectedIndex]);

  useEffect(() => {
    persistLogExplorerFilters(filters);
  }, [filters]);

  // Persist log source configuration whenever it changes (but skip initial mount to avoid overriding saved sources)
  useEffect(() => {
    // Only persist if logSources has been explicitly set (not just initialized)
    // We use a ref to track if this is the first render
    const hasStoredSources = localStorage.getItem(logSourcesStorageKey());
    if (hasStoredSources && logSources.length > 0) {
      try {
        console.log('Auto-persisting log sources:', logSources.map(s => `${s.indexName} (${s.endpoint})`));
        persistLogSources(logSources);
      } catch (error) {
        console.error('Failed to persist log source configuration', error);
      }
    }
  }, [logSources]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.settings-dropdown')) {
        setSettingsDropdownOpen(false);
      }
      if (!event.target.closest('.user-dropdown')) {
        setUserDropdownOpen(false);
      }
      if (logExportMenuRef.current && !logExportMenuRef.current.contains(event.target)) {
        setLogExportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initial load: never auto-dump logs. User must select a Log Source and search.
  useEffect(() => {
    setAllLogs([]);
    setLogs([]);
    setCurrentPage(1);
    setTotalPages(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSavedSearches = async () => {
    try {
      const response = await API.get('/api/saved-search');
      setSavedSearches(response.data);
    } catch (error) {
      console.error('Failed to fetch saved searches:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await API.get('/auth/user-info');
      if (response.data.username) {
        setUserInfo({
          username: response.data.username,
          role: response.data.role || localStorage.getItem('userRole') || 'USER'
        });
        // Update localStorage to keep it in sync
        localStorage.setItem('username', response.data.username);
        if (response.data.role) {
          localStorage.setItem('userRole', response.data.role);
        }
      } else {
        // Fallback to localStorage if API response is invalid
        setUserInfo({
          username: localStorage.getItem('username') || 'User',
          role: localStorage.getItem('userRole') || 'USER'
        });
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // Fallback to localStorage on error
      setUserInfo({
        username: localStorage.getItem('username') || 'User',
        role: localStorage.getItem('userRole') || 'USER'
      });
    }
  };

  const applyDarkMode = () => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleSearch = async () => {
    const { startTime: rangeStart, endTime: rangeEnd } = resolveLogExplorerTimeWindow(filters, appTimezone);
    if (filters.timeMode && filters.timeMode !== 'absolute') {
      setFilters((prev) => (
        prev.startTime === rangeStart && prev.endTime === rangeEnd
          ? prev
          : { ...prev, startTime: rangeStart, endTime: rangeEnd }
      ));
    }
    setLoading(true);
    setSearchError('');
    setSearchMatchTotal(null);
    setTimelineBucketSelection(null);
    try {
      console.log('🔍 Starting search with filters:', {
        logql: filters.logql,
        keyword: filters.keyword,
        selectedIndex: filters.selectedIndex,
        selectedApplication: filters.selectedApplication
      });

      // No Log Source selected → do not pull workspace logs.
      if (!filters.selectedIndex?.trim()) {
        console.log('No Log Source selected — skipping search');
        setSearchError('Select a Log Source (Index) in Filters, then search.');
        setAllLogs([]);
        setLogs([]);
        updatePagination([], filters.size);
        setLoading(false);
        return;
      }

      // No configured endpoints → do not dump workspace seed/demo DB rows.
      if (!logSources || logSources.length === 0) {
        console.log('No log sources configured — skipping workspace DB dump');
        setSearchError('Configure at least one Log Source before searching.');
        setAllLogs([]);
        setLogs([]);
        updatePagination([], filters.size);
        setLoading(false);
        return;
      }

      // Index and application choose which stored services to return.
      const matchedSources = resolveSourcesForSelection(
        logSources,
        filters.selectedIndex,
        filters.selectedApplication
      );
      const sourceKeys = [...new Set(
        matchedSources.flatMap((source) => sourceIdentityKeys(source, !filters.selectedApplication))
      )];

      if (filters.selectedIndex && matchedSources.length === 0) {
        setSearchError(
          `No log source is configured for index "${filters.selectedIndex}"${filters.selectedApplication ? ` / application "${filters.selectedApplication}"` : ''}.`
        );
        setAllLogs([]);
        setLogs([]);
        updatePagination([], filters.size);
        setLoading(false);
        return;
      }

      const externalSources = matchedSources.filter(
        (source) => source.endpoint && !isCommrayLogsEndpoint(source.endpoint)
      );

      // Store each UI source response exactly as received.
      let sourceSyncError = '';
      for (const source of externalSources) {
        const service = sourceServiceLabel(source);
        if (!service || !source.endpoint) {
          continue;
        }
        try {
          await API.post('/api/monitoring/logs/sync/from-url', {
            url: source.endpoint,
            service
          });
        } catch (syncError) {
          console.warn(`Store from configured URL failed for ${source.endpoint}:`, syncError);
          sourceSyncError = syncError?.response?.data?.error
            || syncError?.message
            || `Could not read ${source.endpoint}`;
        }
      }

      // Index and application are source labels. Stored log rows keep their own service
      // field, which is what dashboards query. Do not treat the label as that field.
      // LogQL only when the user typed a LogQL expression.
      // Time-range alone uses /api/monitoring/logs (same auth path as the rest of Log Explorer).
      let effectiveKeyword = filters.keyword?.trim() || '';
      let effectiveLogql = filters.logql && filters.logql.trim();
      if (effectiveLogql && !looksLikeLogQL(effectiveLogql)) {
        // Saved / stale LogQL mode for a plain Trace ID or name — treat as keyword.
        effectiveKeyword = effectiveLogql;
        effectiveLogql = '';
      }
      if (effectiveLogql) {
        try {
          console.log('✅ Using LogQL endpoint with query:', effectiveLogql);
          console.log('📤 Sending POST request to /api/logs/query');
          console.log('📤 Request payload:', { query: effectiveLogql });
          
          const response = await API.post('/api/logs/query', {
            query: effectiveLogql
          });
          
          console.log('📥 LogQL response received:', {
            status: response.status,
            hasLogs: !!response.data?.logs,
            logCount: response.data?.logs?.length || 0,
            total: response.data?.total || 0,
            query: response.data?.query
          });
          
          const filteredLogs = response.data.logs || [];
          console.log(`✅ LogQL query returned ${filteredLogs.length} logs`);
          
          if (filteredLogs.length === 0) {
            console.warn('⚠️ LogQL query returned 0 logs. This might be expected if no logs match the query.');
          }
          
          // Apply time filtering if specified
          let timeFilteredLogs = filteredLogs;
          if (rangeStart || rangeEnd) {
            timeFilteredLogs = filteredLogs.filter(log => {
              const timestamp = log.timestamp || log.time || log.createdAt || log.date;
              if (!timestamp) return false;
              return logMatchesTimeFilters(timestamp, rangeStart, rangeEnd, appTimezone);
            });
            console.log(`Time filtering: ${filteredLogs.length} -> ${timeFilteredLogs.length} logs`);
          }
          
          // Apply additional filters from filters bar (level, keyword, selectedIndex)
          let finalLogs = timeFilteredLogs;
          if (filters.level?.trim()) {
            finalLogs = finalLogs.filter(log => (log.level || '').toLowerCase() === filters.level.trim().toLowerCase());
          }
          if (effectiveKeyword) {
            finalLogs = finalLogs.filter((log) => logMatchesKeyword(log, effectiveKeyword));
          }
          finalLogs = filterLogsForSelectedIndex(
            finalLogs,
            filters.selectedIndex,
            filters.selectedApplication,
            logSources
          );
          // Sort by timestamp (newest first)
          const sorted = finalLogs.sort((a, b) =>
            new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
          );
          
          setAllLogs(sorted);
          updatePagination(sorted, filters.size);
          return;
        } catch (error) {
          console.error('❌ LogQL query failed; treating input as keyword search:', error);
          console.error('Error response:', error.response);
          // Do not fall through unfiltered — use the LogQL text as a keyword search.
          if (!effectiveKeyword) {
            effectiveKeyword = effectiveLogql;
          }
          effectiveLogql = '';
        }
      }

      // Otherwise, use the regular filter-based search
      // Always fetch from backend database first - it contains all logs
      let aggregatedLogs = [];
      
      // CRITICAL: Use a single Map with database ID as key to prevent ANY duplicates
      const allLogsById = new Map();
      
      try {
        // TEMPORARY FIX: Always use test-all endpoint since regular endpoint returns empty
        // This is a workaround until we fix the regular endpoint
        console.log('🔧 WORKAROUND: Using test-all endpoint directly (regular endpoint returns empty)');
        const backendParams = new URLSearchParams();
        
        // CRITICAL: Only add filters if they're explicitly set by user, not from selectedIndex
        // The selectedIndex filter should be applied client-side, not server-side
        // This ensures we get ALL logs from backend, then filter client-side
        
        // Only add keyword if user explicitly entered it
        if (effectiveKeyword) {
          backendParams.append('keyword', effectiveKeyword);
        }
        
        // Only add level if user explicitly selected it
        if (filters.level && filters.level.trim()) {
          backendParams.append('level', filters.level.trim());
        }
        
        // Only add time filters if user explicitly set them
        if (rangeStart && rangeStart.trim()) {
          backendParams.append('startTime', formatFilterTimeForApi(rangeStart.trim(), appTimezone));
        }
        if (rangeEnd && rangeEnd.trim()) {
          backendParams.append('endTime', formatFilterTimeForApi(rangeEnd.trim(), appTimezone));
        }
        
        // CRITICAL: Always request ALL logs from backend (no service filter)
        // We'll filter by service/index client-side after fetching
        backendParams.append('size', '10000');
        backendParams.append('page', '0');
        
        console.log('🔍 Fetching ALL logs from backend (no service filter)');
        console.log('   Selected Index:', filters.selectedIndex || 'none');
        console.log('   User Filters:', {
          keyword: filters.keyword || 'none',
          level: filters.level || 'none',
          service: filters.service || 'none',
          startTime: rangeStart || 'none',
          endTime: rangeEnd || 'none'
        });
        
        console.log('📤 Backend request params:', backendParams.toString());
        // Logs endpoint is permitAll - fetch works with or without token
        const token = localStorage.getItem("token");
        if (token) {
          console.log('Token found, making request to /api/monitoring/logs');
        } else {
          console.log('No token - fetching logs anyway (endpoint is permitAll)');
        }
        console.log('Request URL:', `/api/monitoring/logs?${backendParams.toString()}`);
        
        let backendResponse = { data: { logs: [], total: 0, page: 0, size: 0, hasMore: false } };
        let backendLogs = [];
        
        // Plan retention is enforced server-side. Fetch bounded pages sequentially so we can
        // show thousands of rows without loading the full table in one query (that hung login).
        const logsParams = new URLSearchParams();
        const hasTimeRange = rangeStart?.trim() && rangeEnd?.trim();
        if (rangeStart?.trim()) {
          logsParams.append('startTime', formatFilterTimeForApi(rangeStart.trim(), appTimezone));
        }
        if (rangeEnd?.trim()) {
          logsParams.append('endTime', formatFilterTimeForApi(rangeEnd.trim(), appTimezone));
        }
        if (effectiveKeyword) logsParams.append('keyword', effectiveKeyword);
        if (filters.level?.trim()) logsParams.append('level', filters.level.trim());
        if (filters.service?.trim()) {
          logsParams.append('service', filters.service.trim());
        } else if (sourceKeys.length === 1) {
          logsParams.append('service', sourceKeys[0]);
        } else if (sourceKeys.length > 1) {
          logsParams.append('applications', sourceKeys.join(','));
        }
        console.log('🔍 Fetching logs from /api/monitoring/logs', hasTimeRange ? '(with time range)' : '(plan retention)');
        try {
          let page = 0;
          let serverTotal = 0;
          while (backendLogs.length < LOG_SEARCH_MAX_ROWS && page < 400) {
            const pageParams = new URLSearchParams(logsParams);
            pageParams.set('size', String(LOG_SEARCH_PAGE_SIZE));
            pageParams.set('page', String(page));
            const pageRes = await API.get(`/api/monitoring/logs?${pageParams.toString()}`, { timeout: 20000 });
            backendResponse = pageRes;
            const batch = Array.isArray(pageRes.data?.logs) ? pageRes.data.logs : [];
            if (pageRes.data?.error && batch.length === 0) {
              setSearchError(String(pageRes.data.error));
              break;
            }
            serverTotal = Number(pageRes.data?.total) || serverTotal;
            backendLogs.push(...batch);
            const loadedAll = serverTotal > 0 && backendLogs.length >= serverTotal;
            if (batch.length === 0 || loadedAll) {
              break;
            }
            if (pageRes.data?.hasMore === false && backendLogs.length >= (serverTotal || batch.length)) {
              break;
            }
            page += 1;
          }
          if (serverTotal) {
            setSearchMatchTotal(serverTotal);
          }
          console.log(`✅ Loaded ${backendLogs.length} logs across pages (server total=${serverTotal})`);
          backendResponse.data = {
            logs: backendLogs,
            total: serverTotal || backendLogs.length,
            page: 0,
            size: backendLogs.length,
            hasMore: serverTotal > backendLogs.length
          };
        } catch (backendError) {
          console.error('🚨 CRITICAL: Logs API request failed!', backendError);
          if (backendError.response) {
            console.error('   Status:', backendError.response.status);
            console.error('   Data:', backendError.response.data);
          }
          const timedOut =
            backendError.code === 'ECONNABORTED' ||
            String(backendError.message || '').toLowerCase().includes('timeout');
          setSearchError(
            timedOut
              ? 'Log search timed out. Please retry — a large query was blocking the API.'
              : (backendError.response?.data?.error || backendError.response?.data?.message || 'Failed to search logs.')
          );
          backendLogs = [];
          backendResponse = { data: { logs: [], total: 0, page: 0, size: 0, hasMore: false } };
        }
        
        // Extract logs from response (already processed above, but ensure we have them)
        if (backendLogs.length === 0 && backendResponse.data && backendResponse.data.logs) {
          backendLogs = backendResponse.data.logs || [];
        }
        
        console.log(`📥 Fetched ${backendLogs.length} logs from backend database`);
        console.log('📊 Backend response structure:', {
          total: backendResponse.data?.total || 0,
          page: backendResponse.data?.page || 0,
          size: backendResponse.data?.size || 0,
          hasMore: backendResponse.data?.hasMore || false,
          logsCount: backendLogs.length,
          responseKeys: backendResponse.data ? Object.keys(backendResponse.data) : []
        });
        
        // DEBUG: Check if we're getting the expected number of logs
        if (backendLogs.length < 10) {
          console.error(`🚨 CRITICAL: Only ${backendLogs.length} logs returned from backend!`);
          console.error('📋 Backend response details:', {
            total: backendResponse.data?.total || 0,
            page: backendResponse.data?.page || 0,
            size: backendResponse.data?.size || 0,
            hasMore: backendResponse.data?.hasMore || false,
            logsReturned: backendLogs.length
          });
          
          if ((backendResponse.data?.total || 0) === 0) {
            console.error('💡 DATABASE EMPTY: Backend reports 0 logs in database.');
            console.error('   Use "Generate Sample Logs" button to populate the database.');
          }
        }
        
        // Keep distinct DB rows.
        backendLogs.forEach((log, index) => {
          const logId = log.id != null ? String(log.id) : '';
          const mapped = {
            timestamp: log.timestamp || log.time || log.createdAt || log.date,
            level: log.level || 'INFO',
            service: log.service || 'unknown',
            message: log.message || log.content || '',
            traceId: log.traceId || logId || `log-${index}`,
            id: logId,
            userId: log.userId || '',
            ip: log.ip || '',
            userAgent: log.userAgent || ''
          };
          const uniqueKey = logIdentityKey(mapped);
          if (!allLogsById.has(uniqueKey)) {
            allLogsById.set(uniqueKey, mapped);
          }
        });
        
        console.log(`✅ Mapped ${allLogsById.size} unique logs from backend (using ID-based deduplication)`);
        
        // Convert map to array
        aggregatedLogs = Array.from(allLogsById.values());
        
        console.log(`✅ Mapped ${aggregatedLogs.length} logs from backend`);
        console.log(`   Sample log:`, aggregatedLogs[0] ? {
          id: aggregatedLogs[0].id,
          traceId: aggregatedLogs[0].traceId,
          service: aggregatedLogs[0].service,
          message: aggregatedLogs[0].message?.substring(0, 50)
        } : 'none');
        
        // Selected index and application limit which service names are queried.
        
        // Note: All other filters (service, level, application, keyword, time) are applied later
        // in the unified client-side filtering section after aggregating all logs
      } catch (backendError) {
        console.error('Failed to fetch logs from backend:', backendError);
        if (backendError.response) {
          console.error('Backend error status:', backendError.response.status);
          console.error('Backend error data:', backendError.response.data);
          
          // Handle authentication errors gracefully
          if (backendError.response.status === 401 || backendError.response.status === 403) {
            const token = localStorage.getItem("token");
            if (!token) {
              // No token - definitely not logged in
              console.error('No token found. User needs to log in.');
              alert('Session expired. Please log in again.');
              navigate('/login');
              setLoading(false);
              return;
            } else {
              // Token exists but might be expired or invalid
              console.warn('Token exists but request was rejected. Token might be expired or invalid.');
              console.warn('Error details:', backendError.response.data);
              // Don't redirect - just show empty results and let user try again or refresh
              // The user can manually log out and log back in if needed
            }
          }
        }
        // Continue with empty results if we couldn't fetch from backend
        if (aggregatedLogs.length === 0) {
          console.warn('No logs fetched from backend. Check console for errors.');
        }
      }

      // Server already applied service, level, keyword, and time. Only re-apply
      // application when it is a real DB service and was not sent as `service=`.
      let filteredLogs = aggregatedLogs;
      const beforeFilters = filteredLogs.length;
      const sentServiceToApi = Boolean(filters.service?.trim());
      
      if (filters.service && filters.service.trim() && !sentServiceToApi) {
        const beforeService = filteredLogs.length;
        filteredLogs = filteredLogs.filter(
          log => String(log.service || '').toLowerCase() === filters.service.trim().toLowerCase()
        );
        console.log(`🔍 Client-side filter: service="${filters.service}" - ${beforeService} -> ${filteredLogs.length} logs`);
      }
      
      if (filters.level && filters.level.trim()) {
        const beforeLevel = filteredLogs.length;
        filteredLogs = filteredLogs.filter(log =>
          String(log.level || '').toLowerCase() === filters.level.trim().toLowerCase()
        );
        console.log(`🔍 Client-side filter: level="${filters.level}" - ${beforeLevel} -> ${filteredLogs.length} logs`);
      }
      
      // Filter by keyword (message / traceId / userId — backend already applied when sent)
      if (effectiveKeyword) {
        const beforeKeyword = filteredLogs.length;
        filteredLogs = filteredLogs.filter((log) => logMatchesKeyword(log, effectiveKeyword));
        console.log(`🔍 Client-side filter: keyword="${effectiveKeyword}" - ${beforeKeyword} -> ${filteredLogs.length} logs`);
      }
      
      // Backend already applied startTime/endTime when both were sent — avoid double timezone wipe.
      const backendAppliedTimeRange = Boolean(rangeStart?.trim() && rangeEnd?.trim());
      if (!backendAppliedTimeRange && (rangeStart || rangeEnd)) {
        const beforeTime = filteredLogs.length;
        filteredLogs = filteredLogs.filter(log => {
          const timestamp = log.timestamp || log.time || log.createdAt || log.date;
          if (!timestamp) return false;
          return logMatchesTimeFilters(timestamp, rangeStart, rangeEnd, appTimezone);
        });
        console.log(`🔍 Client-side filter: time range - ${beforeTime} -> ${filteredLogs.length} logs`);
      }
      
      console.log(`📊 Total filters applied: ${beforeFilters} -> ${filteredLogs.length} logs`);
      
      // Use filteredLogs for deduplication
      let timeFilteredLogs = filteredLogs;

      // CRITICAL: Check if we have logs before deduplication
      console.log(`🔍 Before deduplication: ${timeFilteredLogs.length} logs`);
      if (timeFilteredLogs.length === 0) {
        console.error('🚨 CRITICAL: No logs after time filtering!');
        console.error('   Aggregated logs count:', aggregatedLogs.length);
        console.error('   Time filter settings:', { startTime: rangeStart, endTime: rangeEnd });
      }
      
      const finalUniqueLogs = new Map();
      let duplicateCount = 0;
      timeFilteredLogs.forEach((log) => {
        const key = logIdentityKey(log);
        if (!finalUniqueLogs.has(key)) {
          finalUniqueLogs.set(key, log);
        } else {
          duplicateCount++;
        }
      });
      const uniqueLogs = finalUniqueLogs;
      console.log(`🔍 Final deduplication by id: ${timeFilteredLogs.length} -> ${uniqueLogs.size} (${duplicateCount} duplicate ids)`);
      
      // CRITICAL: If we only have 1 log but backend said there are more, something is wrong
      if (uniqueLogs.size === 1 && timeFilteredLogs.length > 1) {
        console.error(`🚨 CRITICAL: Only 1 log after deduplication, but had ${timeFilteredLogs.length} before!`);
        console.error(`   This means deduplication removed ${timeFilteredLogs.length - 1} logs incorrectly.`);
        console.error(`   Sample of removed logs (first 5):`);
        const sampleLogs = Array.from(uniqueLogs.values()).slice(0, 5);
        sampleLogs.forEach((log, idx) => {
          console.error(`   Log ${idx + 1}: traceId=${log.traceId}, id=${log.id}, service=${log.service}, timestamp=${log.timestamp}`);
        });
      }

      const sorted = Array.from(uniqueLogs.values()).sort((a, b) =>
        new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
      );
      const scoped = filterLogsForSelectedIndex(
        sorted,
        filters.selectedIndex,
        filters.selectedApplication,
        logSources
      );

      console.log('📊 Total logs aggregated:', sorted.length, 'from', logSources.length, 'external sources + backend');
      console.log('📊 Breakdown:', {
        fromBackend: aggregatedLogs.length,
        afterTimeFilter: timeFilteredLogs.length,
        afterDeduplication: sorted.length,
        allLogsState: allLogs.length
      });
      
      // CRITICAL: Final check - if we only have 1 log, investigate
      if (sorted.length === 1) {
        console.error(`🚨 CRITICAL: Only 1 log in final result!`);
        console.error(`   Backend returned: ${aggregatedLogs.length} logs`);
        console.error(`   After time filter: ${timeFilteredLogs.length} logs`);
        console.error(`   After deduplication: ${sorted.length} logs`);
        console.error(`   The single log:`, sorted[0]);
        
        // Check if backend actually has more logs
        if (aggregatedLogs.length > 1) {
          console.error(`   ⚠️ Backend returned ${aggregatedLogs.length} logs but only 1 made it through!`);
          console.error(`   This suggests a filtering or deduplication bug.`);
          console.error(`   Sample of logs that were removed:`, aggregatedLogs.slice(0, 5));
        }
        
        // Check if all logs have the same key (causing deduplication)
        const keys = aggregatedLogs.map(log => log.traceId || log.id || 'no-key');
        const uniqueKeys = new Set(keys);
        console.error(`   Unique keys in aggregated logs: ${uniqueKeys.size} out of ${keys.length}`);
        if (uniqueKeys.size < keys.length) {
          console.error(`   ⚠️ Many logs have duplicate keys! This is causing deduplication to remove them.`);
          console.error(`   Sample keys:`, Array.from(uniqueKeys).slice(0, 10));
        }
      }
      
      console.log(`✅ Setting allLogs state with ${sorted.length} logs`);
      console.log(`   First 3 logs:`, sorted.slice(0, 3).map(l => ({ id: l.id, traceId: l.traceId, service: l.service, message: l.message?.substring(0, 30) })));
      
      // CRITICAL: If we have very few logs, check database and offer to generate
      if (sorted.length < 10) {
        console.error('⚠️ CRITICAL: Only', sorted.length, 'logs after aggregation!');
        console.error('This suggests the database might be empty or have very few logs.');
        
        // Check database count and offer to generate logs
        try {
          const countCheck = await API.get('/api/monitoring/logs/count');
          const dbCount = countCheck.data.total || 0;
          setDbLogCount(dbCount); // Store for UI display
          console.error('📊 Database actually has', dbCount, 'logs');
          
          if (dbCount < 10) {
            console.error('💡 SOLUTION: Database is empty or has very few logs.');
            console.error('   Will show generate logs button in UI.');
            // Don't show alert - we'll show a button instead
          } else if (dbCount > sorted.length) {
            console.error('💡 ISSUE: Database has', dbCount, 'logs but only', sorted.length, 'were returned.');
            console.error('   This suggests a filtering or pagination issue.');
          }
        } catch (countErr) {
          console.error('Could not check database count:', countErr);
          setDbLogCount(null);
        }
      }
      
      // CRITICAL DEBUG: Log what we're about to set
      console.log(`🔍 FINAL STEP: About to set allLogs with ${sorted.length} logs`);
      console.log(`   Sorted array length:`, sorted.length);
      console.log(`   Sorted is array:`, Array.isArray(sorted));
      console.log(`   First 5 log IDs:`, sorted.slice(0, 5).map(l => l.id || l.traceId || 'no-id'));

      if (scoped.length === 0 && sourceSyncError) {
        setSearchError(`Could not read the log source. ${sourceSyncError}`);
      }

      setAllLogs(scoped);
      console.log(`✅ Calling updatePagination with ${scoped.length} logs, pageSize=${filters.size}`);
      updatePagination(scoped, filters.size);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
      setAllLogs([]);
      setCurrentPage(1);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };


  const updatePagination = (allLogsData, pageSize) => {
    const total = allLogsData.length;
    // If pageSize is very large (>= 10000), show all logs on one page
    const effectivePageSize = pageSize >= 10000 ? total : pageSize;
    const pages = Math.max(1, Math.ceil(total / effectivePageSize));
    console.log('📄 Updating pagination:', { 
      total, 
      pageSize, 
      effectivePageSize, 
      pages, 
      allLogsDataLength: allLogsData.length,
      allLogsDataType: Array.isArray(allLogsData) ? 'array' : typeof allLogsData
    });
    
    // CRITICAL: Check if allLogsData is actually an array with data
    if (!Array.isArray(allLogsData)) {
      console.error('🚨 CRITICAL: allLogsData is not an array!', allLogsData);
      setTotalPages(1);
      setCurrentPage(1);
      setLogs([]);
      return;
    }
    
    if (allLogsData.length === 0) {
      console.warn('⚠️ allLogsData is empty array');
    }
    
    setTotalPages(pages);
    setCurrentPage(1); // Reset to first page on new search
    console.log(`📄 Calling updateDisplayedLogs with ${allLogsData.length} logs, page=1, pageSize=${effectivePageSize}`);
    updateDisplayedLogs(allLogsData, 1, effectivePageSize);
  };

  const updateDisplayedLogs = (allLogsData, page, pageSize) => {
    console.log(`📋 updateDisplayedLogs called:`, {
      allLogsDataLength: allLogsData?.length || 0,
      allLogsDataIsArray: Array.isArray(allLogsData),
      page,
      pageSize
    });
    
    if (!Array.isArray(allLogsData)) {
      console.error('🚨 CRITICAL: allLogsData is not an array in updateDisplayedLogs!', allLogsData);
      setLogs([]);
      return;
    }
    
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedLogs = allLogsData.slice(startIndex, endIndex);
    
    console.log('📋 Updating displayed logs:', { 
      page, 
      pageSize, 
      startIndex, 
      endIndex, 
      total: allLogsData.length, 
      showing: paginatedLogs.length,
      firstLog: paginatedLogs[0] ? { id: paginatedLogs[0].id, traceId: paginatedLogs[0].traceId, service: paginatedLogs[0].service } : 'none'
    });
    
    // CRITICAL: If we're showing only 1 log but have more, log it
    if (paginatedLogs.length === 1 && allLogsData.length > 1) {
      console.error(`🚨 CRITICAL: Only showing 1 log but have ${allLogsData.length} total!`);
      console.error(`   Page: ${page}, PageSize: ${pageSize}, StartIndex: ${startIndex}, EndIndex: ${endIndex}`);
      console.error(`   This suggests a pagination bug!`);
    }
    
    setLogs(paginatedLogs);
    console.log(`✅ Set logs state with ${paginatedLogs.length} logs`);
  };

  const applyTimelineBucketToResults = useCallback(
    (payload) => {
      if (payload == null || payload.timestamp == null || payload.intervalMs == null) return;
      const bucketStartMs = payload.timestamp;
      const intervalMs = payload.intervalMs;
      const selection = { bucketStartMs, intervalMs };
      const filtered = filterLogsByTimelineBucket(allLogs, selection);
      setTimelineBucketSelection(selection);
      const total = filtered.length;
      const pageSize = filters.size;
      const effectivePageSize = pageSize >= 10000 ? total : pageSize;
      setTotalPages(Math.max(1, Math.ceil(total / (effectivePageSize || 1))));
      setCurrentPage(1);
      updateDisplayedLogs(filtered, 1, effectivePageSize);
    },
    [allLogs, filters.size]
  );

  const clearTimelineBucketFilter = useCallback(() => {
    setTimelineBucketSelection(null);
    updatePagination(allLogs, filters.size);
  }, [allLogs, filters.size]);

  const handlePageSizeChange = (newSize) => {
    const newPageSize = parseInt(newSize, 10);
    const total = visibleLogsForTable.length;
    console.log('Changing page size from', filters.size, 'to', newPageSize, 'Visible logs:', total);
    setFilters((prev) => ({ ...prev, size: newPageSize, page: 0 }));
    const effectivePageSize = newPageSize >= 10000 ? total : newPageSize;
    const pages = Math.max(1, Math.ceil(total / (effectivePageSize || 1)));
    console.log('New total pages:', pages);
    setTotalPages(pages);
    setCurrentPage(1);
    updateDisplayedLogs(visibleLogsForTable, 1, effectivePageSize);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setFilters((prev) => ({ ...prev, page: page - 1 }));
      const total = visibleLogsForTable.length;
      const pageSize = filters.size || 20;
      const effectivePageSize = pageSize >= 10000 ? total : pageSize;
      updateDisplayedLogs(visibleLogsForTable, page, effectivePageSize);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) {
      alert('Please enter a search name');
      return;
    }

    try {
      const searchConfig = {
        filters: {
          service: filters.service,
          level: filters.level,
          keyword: filters.keyword,
          timeMode: filters.timeMode,
          timePreset: filters.timePreset,
          quickAmount: filters.quickAmount,
          quickUnit: filters.quickUnit,
          startTime: filters.startTime,
          endTime: filters.endTime
        },
        pagination: {
          page: filters.page,
          size: filters.size
        }
      };

      await API.post('/api/saved-search', {
        name: saveSearchName,
        description: saveSearchDescription,
        searchConfig: JSON.stringify(searchConfig)
      });

      alert('Search saved successfully!');
      setShowSaveModal(false);
      setSaveSearchName('');
      setSaveSearchDescription('');
      fetchSavedSearches();
    } catch (error) {
      console.error('Failed to save search:', error);
      alert('Failed to save search');
    }
  };

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const handleSearchRef = useRef(null);
  handleSearchRef.current = handleSearch;
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  useEffect(() => {
    const amount = Number(filters.refreshAmount);
    if (!Number.isFinite(amount) || amount < 1) return undefined;
    const unit = REFRESH_UNITS.find((item) => item.id === filters.refreshUnit) || REFRESH_UNITS[1];
    const timer = setInterval(() => {
      if (loadingRef.current) return;
      if (!filtersRef.current?.selectedIndex) return;
      handleSearchRef.current?.();
    }, amount * unit.ms);
    return () => clearInterval(timer);
  }, [filters.refreshAmount, filters.refreshUnit]);

  const handleLoadSavedSearch = (search) => {
    setTimelineBucketSelection(null);
    const config = JSON.parse(search.searchConfig);
    const f = config.filters || {};
    setFilters((prev) => ({
      ...prev,
      ...f,
      startTime: f.startTime ? normalizeDatetimeLocalFilterValue(f.startTime) : '',
      endTime: f.endTime ? normalizeDatetimeLocalFilterValue(f.endTime) : '',
      page: config.pagination.page,
      size: config.pagination.size
    }));
  };

  const handleFilterChange = (field, value) => {
    let next = value;
    if (field === 'startTime' || field === 'endTime') {
      if (typeof value === 'string') {
        const t = value.trim();
        next = t ? normalizeDatetimeLocalFilterValue(t) : '';
      }
    }
    setFilters((prev) => ({
      ...prev,
      [field]: next
    }));
  };

  const clearFilters = () => {
    setTimelineBucketSelection(null);
    setFilters({ ...DEFAULT_LOG_EXPLORER_FILTERS });
    setAllLogs([]);
    setLogs([]);
    setCurrentPage(1);
    setTotalPages(1);
    setSearchError('');
    setSearchMatchTotal(null);
  };

  // Indices the user created under Log Sources.
  const getAvailableIndexOptions = () => {
    if (!logSources || logSources.length === 0) {
      return [];
    }
    const indices = new Set();
    logSources.forEach((source) => {
      if (source.indexName) {
        indices.add(source.indexName);
      }
    });
    return Array.from(indices)
      .sort()
      .map((indexName) => ({ indexName, label: indexName }));
  };

  // Applications configured under the selected index.
  const getApplicationsForIndex = (indexName) => {
    if (!indexName || !logSources || logSources.length === 0) {
      return [];
    }
    const applications = new Set();
    logSources.forEach((source) => {
      if (source.indexName !== indexName) {
        return;
      }
      const names = [
        source.applicationName,
        ...(Array.isArray(source.applications) ? source.applications : [])
      ];
      names.forEach((app) => {
        if (app && String(app).trim()) {
          applications.add(String(app).trim());
        }
      });
    });
    return Array.from(applications).sort();
  };

  const handleIndexChange = (indexName) => {
    setFilters((prev) => ({
      ...prev,
      selectedIndex: indexName,
      selectedApplication: ''
    }));
  };

  const handleLogExportFormat = useCallback(
    async (format) => {
      setLogExportMenuOpen(false);
      const data = visibleLogsForTable;
      if (!data.length) return;
      const stamp = slugifyFilenamePart(new Date().toISOString().slice(0, 19));
      const formatTs = (ts) => formatTimestampWithMillisUser(ts, appTimezone);
      try {
        if (format === 'csv') {
          await saveTextFileWithPicker(
            logsToCsvContent(data, formatTs),
            `logs-${stamp}.csv`,
            'text/csv;charset=utf-8',
            SAVE_PICKER_TYPES_CSV
          );
        } else {
          await saveTextFileWithPicker(
            logsToPlainTextContent(data, formatTs),
            `logs-${stamp}.txt`,
            'text/plain;charset=utf-8',
            SAVE_PICKER_TYPES_TXT
          );
        }
      } catch (e) {
        console.error('Log export failed', e);
      }
    },
    [visibleLogsForTable]
  );

  const handlePrintPage = useCallback(() => {
    window.print();
  }, []);

  // Timeline: bucket size follows selected search timeframe (see getTimelineIntervalMs).
  const { timelineData, timelineColumnNotice } = useMemo(() => {
    if (!allLogs || allLogs.length === 0) {
      return { timelineData: [], timelineColumnNotice: null };
    }

    let filteredLogs = allLogs;
    if (filters.startTime || filters.endTime) {
      filteredLogs = allLogs.filter((log) => {
        const timestamp = log.timestamp || log.time || log.createdAt || log.date;
        if (!timestamp) return false;
        return logMatchesTimeFilters(timestamp, filters.startTime, filters.endTime, appTimezone);
      });
    }

    let rangeStartMs;
    let rangeEndMs;
    let timeRangeMs;
    if (filters.startTime && filters.endTime) {
      rangeStartMs =
        parseDatetimeLocalFilterToMillis(filters.startTime, appTimezone) ?? Date.now() - 3600000;
      rangeEndMs = parseDatetimeLocalFilterToMillis(filters.endTime, appTimezone) ?? Date.now();
      timeRangeMs = rangeEndMs - rangeStartMs;
    } else {
      let minTs = Infinity;
      let maxTs = -Infinity;
      filteredLogs.forEach((log) => {
        const ts = log.timestamp || log.time || log.createdAt || log.date;
        if (ts) {
          const t = parseLogTimestampToMillis(ts);
          if (t != null) {
            minTs = Math.min(minTs, t);
            maxTs = Math.max(maxTs, t);
          }
        }
      });
      rangeStartMs = isFinite(minTs) ? minTs : Date.now() - 3600000;
      rangeEndMs = isFinite(maxTs) ? maxTs : Date.now();
      timeRangeMs = rangeEndMs - rangeStartMs;
    }

    let intervalMs = getTimelineIntervalMs(timeRangeMs);
    if (!intervalMs || intervalMs <= 0) intervalMs = 1000;

    let alignedStart = Math.floor(rangeStartMs / intervalMs) * intervalMs;
    let alignedEnd = Math.ceil(rangeEndMs / intervalMs) * intervalMs;
    let nb = countTimelineBuckets(alignedStart, alignedEnd, intervalMs);
    if (nb > MAX_TIMELINE_BUCKETS) {
      const span = Math.max(alignedEnd - alignedStart, intervalMs);
      intervalMs = Math.max(1, Math.ceil(span / (MAX_TIMELINE_BUCKETS - 1)));
      alignedStart = Math.floor(rangeStartMs / intervalMs) * intervalMs;
      alignedEnd = Math.ceil(rangeEndMs / intervalMs) * intervalMs;
    }

    const columnNotice = formatTimelineColumnNotice(intervalMs);

    const countsByBucket = {};
    filteredLogs.forEach((log) => {
      const ts = log.timestamp || log.time || log.createdAt || log.date;
      if (!ts) return;
      try {
        const t = parseLogTimestampToMillis(ts);
        if (t == null) return;
        const bucket = Math.floor(t / intervalMs) * intervalMs;
        countsByBucket[bucket] = (countsByBucket[bucket] || 0) + 1;
      } catch {
        /* skip */
      }
    });

    const sortedData = [];

    for (let bucket = alignedStart; bucket <= alignedEnd; bucket += intervalMs) {
      const count = countsByBucket[bucket] || 0;
      const date = new Date(bucket);
      sortedData.push({
        time: formatTimelineBucketLabelUser(date, intervalMs, appTimezone),
        total: count,
        timestamp: bucket,
        intervalMs
      });
    }

    return { timelineData: sortedData, timelineColumnNotice: columnNotice };
  }, [allLogs, filters.startTime, filters.endTime, appTimezone]);

  const timelineXTickIndices = useMemo(
    () => buildTimelineSectionTickIndices(timelineData.length),
    [timelineData]
  );

  const timelineBarLayout = useMemo(() => getTimelineBarLayout(timelineData.length), [timelineData]);

  const renderTimelineXAxisTick = useCallback(
    (props) => <LogTimelineXAxisTick {...props} visibleIndices={timelineXTickIndices} />,
    [timelineXTickIndices]
  );

  /**
   * Recharts 3: Bar onClick receives the bar rectangle item (includes timestamp, intervalMs, payload).
   * Chart-level onClick is (nextState, reactEvent) — no activePayload; use that for Line + axis index.
   */
  const handleTimelineLineChartClick = useCallback(
    (nextState) => {
      const rawIdx = nextState?.activeTooltipIndex ?? nextState?.activeIndex;
      if (rawIdx === undefined || rawIdx === null) return;
      const idx = typeof rawIdx === 'string' ? parseInt(rawIdx, 10) : Number(rawIdx);
      if (!Number.isFinite(idx) || idx < 0 || idx >= timelineData.length) return;
      const row = timelineData[idx];
      if (row?.timestamp != null && row?.intervalMs != null) {
        applyTimelineBucketToResults(row);
      }
    },
    [timelineData, applyTimelineBucketToResults]
  );

  const handleTimelineBarClick = useCallback(
    (barData, index) => {
      const row = timelinePayloadFromBarClick(barData, index, timelineData);
      if (row) applyTimelineBucketToResults(row);
    },
    [applyTimelineBucketToResults, timelineData]
  );

  // Debug: Log timeline data when it changes
  useEffect(() => {
    console.log('All Logs:', allLogs.length);
    console.log('Filters:', filters);
    console.log('Timeline Data:', timelineData);
    console.log('Timeline Data Length:', timelineData?.length);
  }, [allLogs, filters.startTime, filters.endTime, timelineData]);

  const createEditingSource = (source) => ({
    id: source.id || `source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    indexName: source.indexName || '',
    applicationName: source.applicationName || (Array.isArray(source.applications) ? source.applications[0] : '') || '',
    applicationsText: Array.isArray(source.applications)
      ? source.applications.join(', ')
      : source.applications || '',
    environment: source.environment || 'OTHER',
    platform: source.platform || 'OTHER',
    collectionMethod: source.collectionMethod || 'HTTP_API',
    endpoint: source.endpoint || '',
    collector: source.collector || '',
    serviceName: source.serviceName || '',
    tagsText: tagsToText(source.tags),
    originalEndpoint: source.endpoint || ''
  });

  const createNewEditingSource = () => ({
    id: `source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    indexName: '',
    applicationName: '',
    applicationsText: '',
    environment: 'PRODUCTION',
    platform: 'OTHER',
    collectionMethod: 'HTTP_API',
    endpoint: '',
    collector: '',
    serviceName: '',
    tagsText: '',
    originalEndpoint: ''
  });

  const handleStartEdit = () => {
    setEditingSources((logSources || []).map(createEditingSource));
    setIsEditMode(true);
    setSourcesError('');
  };

  const handleAddSource = () => {
    setEditingSources((prev) => [...prev, createNewEditingSource()]);
  };

  const handleSourceFieldChange = (id, field, value) => {
    setEditingSources((prev) =>
      prev.map((source) => {
        if (source.id !== id) {
          return source;
        }
        const next = { ...source, [field]: value };
        if (field === 'endpoint' && value) {
          if (!next.indexName?.trim()) {
            next.indexName = deriveDefaultIndexFromEndpoint(value);
          }
          if (!next.applicationName?.trim()) {
            next.applicationName = deriveDefaultApplicationFromEndpoint(value);
          }
        }
        if (field === 'applicationName') {
          next.applicationsText = value;
        }
        return next;
      })
    );
    if (sourcesError) {
      setSourcesError('');
    }
  };

  const handleRemoveSource = (id) => {
    setEditingSources((prev) => prev.filter((source) => source.id !== id));
  };

  const handleSaveSources = async () => {
    const sanitizedSources = editingSources.reduce(
      (acc, source) => {
        const endpoint = (source.endpoint || '').trim();
        const applicationName = (source.applicationName || source.applicationsText || '').trim();
        const namedApplications = String(source.applicationsText || '')
          .split(',')
          .map((app) => app.trim())
          .filter(Boolean);
        const applications = applicationName
          ? [applicationName, ...namedApplications.filter((app) => app !== applicationName)]
          : namedApplications;

        if (!endpoint) {
          acc.invalidCount += 1;
          return acc;
        }

        const parsedTags = parseTagsText(source.tagsText);
        if (parsedTags.error) {
          acc.tagError = parsedTags.error;
          return acc;
        }

        const indexName = (source.indexName || deriveDefaultIndexFromEndpoint(endpoint)).trim();

        acc.valid.push(
          normalizeStoredSource({
            id: source.id || `source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            indexName,
            applicationName,
            applications: applications.length > 0 ? applications : [deriveDefaultApplicationFromEndpoint(endpoint)],
            environment: source.environment,
            platform: source.platform,
            collectionMethod: source.collectionMethod,
            endpoint,
            collector: source.collector,
            serviceName: source.serviceName,
            tags: parsedTags.tags
          })
        );

        return acc;
      },
      { valid: [], invalidCount: 0, tagError: '' }
    );

    if (sanitizedSources.tagError) {
      setSourcesError(sanitizedSources.tagError);
      return;
    }

    if (sanitizedSources.valid.length === 0) {
      setSourcesError('Please provide at least one source URL before saving.');
      return;
    }

    if (sanitizedSources.invalidCount > 0) {
      setSourcesError('Some sources were skipped because no source URL was provided.');
    } else {
      setSourcesError('');
    }

    const sourcesToSave = migrateLogSourcesModel(sanitizedSources.valid);
    persistLogSources(sourcesToSave);
    setLogSources(sourcesToSave);
    setIsEditMode(false);
    setEditingSources([]);

    try {
      const response = await API.put('/api/monitoring/log-sources', { sources: sourcesToSave });
      if (Array.isArray(response.data) && response.data.length > 0) {
        const saved = migrateLogSourcesModel(response.data.map((source) => normalizeStoredSource(source)).filter(Boolean));
        persistLogSources(saved);
        setLogSources(saved);
      }
    } catch (error) {
      const message = error?.response?.data?.error || 'Saved in this browser, but the server did not store the sources.';
      setSourcesError(message);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingSources([]);
    setSourcesError('');
  };

  return (
    <div className="log-explorer">
      {/* Fixed Header with Blue Background */}
      <div className="dashboard-header commray-no-print">
        <div className="header-content">
          {/* Commray Logo - Left Side */}
          <div className="brand-section">
            <h1 className="brand-name">Commray</h1>
          </div>

          {/* Log Explorer Title - Center */}
          <div className="center-title">
            <h2 className="dashboard-title-header">Log Explorer</h2>
          </div>

          {/* Right Controls */}
          <div className="right-controls">
            <AskCommrayAi
              onUseInExplorer={(draft) => {
                const result = draftSearchFilters(filters, draft, logSources);
                if (result.error) {
                  setSearchError(result.error);
                  return result.error;
                }
                setSearchError('');
                setFilters(result.filters);
                return '';
              }}
            />
            {/* Back Button */}
            <button 
              onClick={handleBackToDashboard}
              className="back-button"
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <button
              onClick={handleStartEdit}
              className="edit-toggle-button"
              type="button"
            >
              <Edit3 size={16} />
              Edit
            </button>

            {/* Settings Dropdown */}
            <div className="settings-dropdown">
              <button 
                onClick={() => setSettingsDropdownOpen(!settingsDropdownOpen)}
                className="settings-button"
              >
                <Settings size={20} />
              </button>
              {settingsDropdownOpen && (
                <div className="dropdown-menu">
                  <button 
                    onClick={() => {
                      setSettingsDropdownOpen(false);
                      // Settings functionality can be added here
                    }}
                    className="dropdown-item"
                  >
                    <Settings size={16} style={{ marginRight: '8px' }} />
                    Settings
                  </button>
                  <button 
                    onClick={() => {
                      toggleDarkMode();
                      setSettingsDropdownOpen(false);
                    }}
                    className="dropdown-item"
                  >
                    {darkMode ? <Sun size={16} style={{ marginRight: '8px' }} /> : <Moon size={16} style={{ marginRight: '8px' }} />}
                    {darkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>
                  <UserTimezoneSelect />
                </div>
              )}
            </div>

            {/* User Dropdown */}
            <div className="user-dropdown">
              <button 
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="user-button"
              >
                <div className="user-avatar">
                  <User size={16} />
                </div>
                <div className="user-info">
                  <div className="user-name">{userInfo.username || 'User'}</div>
                  <div className="user-role">{userInfo.role || localStorage.getItem('userRole') || 'USER'}</div>
                </div>
              </button>
              {userDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-item" style={{ cursor: 'default', backgroundColor: '#f9fafb' }}>
                    <UserCircle size={16} style={{ marginRight: '8px' }} />
                    <div>
                      <div style={{ fontWeight: '600' }}>{userInfo.username || 'User'}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{userInfo.role || localStorage.getItem('userRole') || 'USER'}</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setUserDropdownOpen(false);
                      navigate('/settings/profile');
                    }}
                    className="dropdown-item"
                  >
                    <User size={16} style={{ marginRight: '8px' }} />
                    Settings
                  </button>
                  <button 
                    onClick={() => {
                      setUserDropdownOpen(false);
                      navigate('/change-password');
                    }}
                    className="dropdown-item"
                  >
                    <Key size={16} style={{ marginRight: '8px' }} />
                    Change Password
                  </button>
                  <button 
                    onClick={() => {
                      setUserDropdownOpen(false);
                      handleLogout();
                    }}
                    className="dropdown-item"
                  >
                    <LogOut size={16} style={{ marginRight: '8px' }} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="log-content">
        {/* Horizontal Search Field at Top */}
        <div className="log-search-section commray-no-print">
          <div className="log-search-container">
            <div className="log-search-input-wrapper">
              <Search className="log-search-icon" size={20} />
              <LogQueryEditor
                value={filters.logql || filters.keyword}
                logqlMode={Boolean(filters.logql)}
                onSearch={handleSearch}
                onChange={(value) => {
                  const isLogQL = looksLikeLogQL(value);
                  if (isLogQL) {
                    handleFilterChange('logql', value);
                    handleFilterChange('keyword', '');
                  } else {
                    handleFilterChange('keyword', value);
                    handleFilterChange('logql', '');
                  }
                }}
              />
              {filters.logql && (
                <span style={{ fontSize: '12px', color: '#3b82f6', marginLeft: '8px', fontWeight: '500' }}>
                  LogQL Mode
                </span>
              )}
              <button
                onClick={handleSearch}
                disabled={loading}
                className="log-search-button"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Timeline Visualization */}
        <div className="log-timeline-section">
          <div className="log-timeline-container">
            <div className="log-timeline-header">
              <h3 className="log-timeline-title">Log Timeline</h3>
              <div className="log-chart-type-selector commray-no-print">
                <button
                  onClick={() => setChartType('line')}
                  className={`chart-type-button ${chartType === 'line' ? 'active' : ''}`}
                  title="Line Chart"
                >
                  <TrendingUp size={18} />
                </button>
                <button
                  onClick={() => setChartType('bar')}
                  className={`chart-type-button ${chartType === 'bar' ? 'active' : ''}`}
                  title="Bar Chart"
                >
                  <BarChart3 size={18} />
                </button>
              </div>
            </div>
            {timelineColumnNotice && timelineData?.length > 0 && (
              <p className="log-timeline-resolution" role="note">
                Each column represents {timelineColumnNotice}.
              </p>
            )}
            {timelineData?.length > 0 && (
              <p className="log-timeline-hint" role="note">
                Click a bar or point to show only logs from that time column in the results below.
              </p>
            )}
            {timelineBucketSelection && (
              <div className="log-timeline-bucket-active">
                <span>Log results are limited to the selected timeline column.</span>
                <button
                  type="button"
                  className="log-timeline-bucket-clear"
                  onClick={clearTimelineBucketFilter}
                >
                  Show all results
                </button>
              </div>
            )}
            <div className="log-timeline-chart">
              {timelineData && timelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={175}>
                  {chartType === 'line' ? (
                    <LineChart data={timelineData} onClick={handleTimelineLineChartClick}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        interval={0}
                        height={80}
                        tickLine={false}
                        tick={renderTimelineXAxisTick}
                      />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#2563eb"
                        strokeWidth={2}
                        name="Total Logs"
                        dot={{ r: 3, cursor: 'pointer' }}
                        activeDot={{ r: 5, cursor: 'pointer' }}
                      />
                    </LineChart>
                  ) : (
                    <BarChart
                      data={timelineData}
                      barCategoryGap={timelineBarLayout.barCategoryGap}
                      onClick={handleTimelineLineChartClick}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        interval={0}
                        height={80}
                        tickLine={false}
                        tick={renderTimelineXAxisTick}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="total"
                        fill="#2563eb"
                        name="Total Logs"
                        maxBarSize={timelineBarLayout.maxBarSize}
                        cursor="pointer"
                        onClick={handleTimelineBarClick}
                      >
                        {timelineData.map((entry) => (
                          <Cell
                            key={`tl-${entry.timestamp}`}
                            fill={
                              timelineBucketSelection?.bucketStartMs === entry.timestamp
                                ? '#1d4ed8'
                                : '#2563eb'
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <div className="log-timeline-empty">
                  <p>No timeline data available. Search for logs to see the timeline visualization.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="log-grid">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1 log-explorer-print-hide">
            <div className="bg-white rounded-lg shadow-sm border p-6 log-filters-scroll-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Filters</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                {/* Log Sources - Index Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Log Sources (Index)
                  </label>
                  <select
                    value={filters.selectedIndex}
                    onChange={(e) => handleIndexChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a Log Source</option>
                    {getAvailableIndexOptions().map((option) => (
                      <option key={option.indexName} value={option.indexName}>{option.label}</option>
                    ))}
                  </select>
                  {!filters.selectedIndex && (
                    <p className="mt-1 text-xs text-gray-500">Required — results load only for the selected source</p>
                  )}
                </div>

                {/* Applications Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applications
                  </label>
                  <select
                    value={filters.selectedApplication}
                    onChange={(e) => handleFilterChange('selectedApplication', e.target.value)}
                    disabled={!filters.selectedIndex}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">All Applications</option>
                    {getApplicationsForIndex(filters.selectedIndex).map((app) => (
                      <option key={app} value={app}>{app}</option>
                    ))}
                  </select>
                  {!filters.selectedIndex && (
                    <p className="mt-1 text-xs text-gray-500">Select an index first</p>
                  )}
                </div>

                {/* Service Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service
                  </label>
                  <select
                    value={filters.service}
                    onChange={(e) => handleFilterChange('service', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Services</option>
                    <option value="api-gateway">API Gateway</option>
                    <option value="user-service">User Service</option>
                    <option value="order-service">Order Service</option>
                    <option value="payment-service">Payment Service</option>
                    <option value="notification-service">Notification Service</option>
                    <option value="auth-service">Auth Service</option>
                  </select>
                </div>

                {/* Log Level Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Log Level
                  </label>
                  <select
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Levels</option>
                    <option value="ERROR">Error</option>
                    <option value="WARN">Warning</option>
                    <option value="INFO">Info</option>
                    <option value="DEBUG">Debug</option>
                    <option value="TRACE">Trace</option>
                  </select>
                </div>

                {/* Time Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time range
                  </label>
                  <select
                    value={filters.timeMode || 'absolute'}
                    onChange={(e) => handleFilterChange('timeMode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="preset">Presets</option>
                    <option value="quick">Quick select</option>
                    <option value="absolute">Date &amp; time range</option>
                  </select>
                </div>

                {(filters.timeMode || 'absolute') === 'preset' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preset
                    </label>
                    <select
                      value={filters.timePreset || '15m'}
                      onChange={(e) => handleFilterChange('timePreset', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {TIME_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>{preset.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {filters.timeMode === 'quick' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={String(filters.quickAmount || 15)}
                        onChange={(e) => handleFilterChange('quickAmount', Number(e.target.value))}
                        className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Quick select amount"
                      >
                        {QUICK_AMOUNTS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <select
                        value={filters.quickUnit || 'minutes'}
                        onChange={(e) => handleFilterChange('quickUnit', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Quick select unit"
                      >
                        {QUICK_UNITS.map((unit) => (
                          <option key={unit.id} value={unit.id}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(filters.timeMode || 'absolute') === 'absolute' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start time
                      </label>
                      <input
                        type="datetime-local"
                        step={0.001}
                        value={filters.startTime}
                        onChange={(e) => handleFilterChange('startTime', e.target.value)}
                        title="Includes seconds and milliseconds"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Uses your selected timezone (Settings)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End time
                      </label>
                      <input
                        type="datetime-local"
                        step={0.001}
                        value={filters.endTime}
                        onChange={(e) => handleFilterChange('endTime', e.target.value)}
                        title="Includes seconds and milliseconds"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Uses your selected timezone (Settings). Inclusive up to this instant.</p>
                    </div>
                  </>
                )}

                {(filters.timeMode || 'absolute') !== 'absolute' && (
                  <p className="text-xs text-gray-500">
                    {(() => {
                      const windowRange = resolveLogExplorerTimeWindow(filters, appTimezone);
                      if (!windowRange.startTime || !windowRange.endTime) return 'Uses your selected timezone (Settings).';
                      return `${windowRange.startTime.replace('T', ' ')} → ${windowRange.endTime.replace('T', ' ')}`;
                    })()}
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refresh every
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={filters.refreshAmount === '' || filters.refreshAmount == null ? '' : String(filters.refreshAmount)}
                      onChange={(e) => handleFilterChange('refreshAmount', e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Refresh interval amount"
                    >
                      <option value="">Off</option>
                      {QUICK_AMOUNTS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <select
                      value={filters.refreshUnit || 'minutes'}
                      onChange={(e) => handleFilterChange('refreshUnit', e.target.value)}
                      disabled={!filters.refreshAmount}
                      className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      aria-label="Refresh interval unit"
                    >
                      {REFRESH_UNITS.map((unit) => (
                        <option key={unit.id} value={unit.id}>{unit.label}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Repeats Search while a log source is selected.</p>
                </div>

                {/* Search Button */}
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Search Logs
                </button>
              </div>

              {/* Saved Searches */}
              {savedSearches.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Saved Searches</h4>
                  <div className="space-y-2">
                    {savedSearches.map((search) => (
                      <button
                        key={search.id}
                        onClick={() => handleLoadSavedSearch(search)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100"
                      >
                        <div className="font-medium">{search.name}</div>
                        {search.description && (
                          <div className="text-xs text-gray-500">{search.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="log-sources-section">
                <div className="log-sources-header">
                  <div>
                    <h3 className="log-sources-title">Log Sources</h3>
                    <p className="log-sources-subtitle">One source model for every platform. Logs stay in this workspace.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className="add-source-button"
                  >
                    <Edit3 size={16} />
                    Edit
                  </button>
                </div>

                {sourcesError && !isEditMode && (
                  <div className="log-sources-error">{sourcesError}</div>
                )}

                <div className="log-sources-list">
                  <LogSourceTree
                    sources={logSources}
                    environmentLabel={(value) => logSourceLabel(LOG_SOURCE_ENVIRONMENTS, value)}
                    platformLabel={(value) => logSourceLabel(LOG_SOURCE_PLATFORMS, value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="lg:col-span-3 log-explorer-print-main">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b log-results-toolbar">
                <div className="log-results-toolbar-row">
                  <div className="log-results-toolbar-left">
                    <div className="log-results-header-text">
                      <h3 className="text-lg font-medium text-gray-900 log-results-header-title">
                        Log Results ({visibleLogsForTable.length} total entries)
                      </h3>
                      {!loading && logResultsPagingLabel && (
                        <div className="text-sm text-gray-600 log-results-header-note">{logResultsPagingLabel}</div>
                      )}
                    </div>
                    {!loading && visibleLogsForTable.length > 0 && (
                      <div className="commray-no-print log-results-header-actions log-explorer-header-actions">
                        <div className="log-export-dropdown" ref={logExportMenuRef}>
                          <button
                            type="button"
                            onClick={() => setLogExportMenuOpen((o) => !o)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                            aria-expanded={logExportMenuOpen}
                            aria-haspopup="menu"
                          >
                            <Download size={14} aria-hidden />
                            Export
                            <ChevronDown
                              size={14}
                              aria-hidden
                              className={logExportMenuOpen ? 'log-export-chevron--open' : ''}
                            />
                          </button>
                          {logExportMenuOpen && (
                            <div className="log-export-dropdown-menu" role="menu">
                              <button
                                type="button"
                                role="menuitem"
                                className="log-export-dropdown-item"
                                onClick={() => void handleLogExportFormat('csv')}
                              >
                                <span className="log-export-dropdown-label">CSV</span>
                                <span className="log-export-dropdown-hint">.csv — spreadsheet apps</span>
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="log-export-dropdown-item"
                                onClick={() => void handleLogExportFormat('text')}
                              >
                                <span className="log-export-dropdown-label">Plain text</span>
                                <span className="log-export-dropdown-hint">.txt — logs as readable text</span>
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handlePrintPage}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                        >
                          <Printer size={14} aria-hidden />
                          Print
                        </button>
                      </div>
                    )}
                  </div>

                  {!loading && allLogs.length > 0 && (
                    <div className="commray-no-print log-results-toolbar-right">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <label className="text-sm text-gray-700 whitespace-nowrap" htmlFor="log-explorer-page-size">
                          Show:
                        </label>
                        <select
                          id="log-explorer-page-size"
                          value={filters.size}
                          onChange={(e) => handlePageSizeChange(e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={500}>500</option>
                          <option value={1000}>1000</option>
                          <option value={10000}>All</option>
                        </select>
                      </div>

                      {totalPages > 0 && (
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        {/* Previous button */}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="p-1.5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Previous page"
                        >
                          <ChevronLeft size={18} />
                        </button>

                        {/* Page numbers with ellipsis */}
                        {(() => {
                          const pages = [];
                          const maxVisible = 9;
                          
                          if (totalPages <= maxVisible) {
                            // Show all pages if 9 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Always show first page
                            pages.push(1);
                            
                            if (currentPage <= 5) {
                              // Show first 7 pages, then ellipsis, then last page
                              for (let i = 2; i <= 7; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            } else if (currentPage >= totalPages - 4) {
                              // Show first page, ellipsis, then last 7 pages
                              pages.push('ellipsis');
                              for (let i = totalPages - 6; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // Show first page, ellipsis, current-2 to current+2, ellipsis, last page
                              pages.push('ellipsis');
                              for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, index) => {
                            if (page === 'ellipsis') {
                              return (
                                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                  ...
                                </span>
                              );
                            }

                            const isActive = currentPage === page;
                            return (
                              <button
                                key={page}
                                type="button"
                                onClick={() => handlePageChange(page)}
                                className={isActive ? undefined : 'log-pagination-page'}
                                style={isActive ? {
                                  backgroundColor: '#2563eb',
                                  color: '#ffffff',
                                  fontWeight: '600',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  padding: '4px 10px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: 'pointer'
                                } : undefined}
                              >
                                {page}
                              </button>
                            );
                          });
                        })()}

                        {/* Next button */}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="p-1.5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Next page"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    )}
                    </div>
                  )}

                  {!loading && allLogs.length === 0 && (
                    <div className="commray-no-print log-results-toolbar-right">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <label className="text-sm text-gray-700 whitespace-nowrap" htmlFor="log-explorer-page-size-empty">
                          Show:
                        </label>
                        <select
                          id="log-explorer-page-size-empty"
                          value={filters.size}
                          onChange={(e) => handlePageSizeChange(e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={500}>500</option>
                          <option value={1000}>1000</option>
                          <option value={10000}>All</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-12">
                    {timelineBucketSelection && allLogs.length > 0 ? (
                      <div className="text-gray-600">
                        <p className="mb-3">No log events fall in this timeline column.</p>
                        <button
                          type="button"
                          onClick={clearTimelineBucketFilter}
                          className="text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          Show all search results
                        </button>
                      </div>
                    ) : !logSources || logSources.length === 0 ? (
                      <div className="text-gray-600">
                        <p className="mb-2 font-medium text-gray-700">No log sources configured.</p>
                        <p className="text-sm text-gray-500">
                          Add an endpoint under Log Sources, then run a search.
                        </p>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        {searchError ? (
                          searchError
                        ) : (
                          <>
                            No logs found for the selected source within your plan retention window.
                            {isFreePlan
                              ? ' Free plan keeps the last 7 days.'
                              : ' Starter keeps the last 14 days.'}
                            {' '}Try adjusting filters or the date range.
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <LogResultList
                    logs={logs}
                    formatTimestamp={(timestamp) => formatTimestampWithMillisUser(timestamp, appTimezone)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditMode && (
        <div
          className="modal-overlay log-sources-edit-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelEdit();
            }
          }}
        >
          <div
            className="modal log-sources-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="log-sources-edit-title"
          >
            <div className="modal-header">
              <h3 id="log-sources-edit-title" className="modal-title">Edit Log Sources</h3>
              <button
                type="button"
                className="modal-close"
                onClick={handleCancelEdit}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body log-sources-edit-modal-body">
              <div className="log-sources-edit-toolbar">
                <p className="log-sources-edit-hint">
                  Every source uses the same Log Explorer. Platform says where the app runs.
                  Collection method says how logs arrive. Existing sources only need a source URL;
                  the other fields can stay at their defaults.
                </p>
                <button
                  type="button"
                  onClick={handleAddSource}
                  className="add-source-button"
                >
                  <PlusCircle size={16} />
                  Add source
                </button>
              </div>

              {sourcesError && (
                <div className="log-sources-error">{sourcesError}</div>
              )}

              {editingSources.length > 0 ? (
                <div className="log-sources-edit-list">
                  {editingSources.map((source) => (
                    <div key={source.id} className="log-source-edit-card">
                      <div className="log-source-field">
                        <label htmlFor={`log-source-app-${source.id}`}>Application Name</label>
                        <input
                          id={`log-source-app-${source.id}`}
                          type="text"
                          value={source.applicationName}
                          onChange={(e) => handleSourceFieldChange(source.id, 'applicationName', e.target.value)}
                          placeholder="Payment API"
                        />
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-env-${source.id}`}>Environment</label>
                        <select
                          id={`log-source-env-${source.id}`}
                          value={source.environment}
                          onChange={(e) => handleSourceFieldChange(source.id, 'environment', e.target.value)}
                        >
                          {LOG_SOURCE_ENVIRONMENTS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-platform-${source.id}`}>Platform</label>
                        <select
                          id={`log-source-platform-${source.id}`}
                          value={source.platform}
                          onChange={(e) => handleSourceFieldChange(source.id, 'platform', e.target.value)}
                        >
                          {LOG_SOURCE_PLATFORMS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-method-${source.id}`}>Collection Method</label>
                        <select
                          id={`log-source-method-${source.id}`}
                          value={source.collectionMethod}
                          onChange={(e) => handleSourceFieldChange(source.id, 'collectionMethod', e.target.value)}
                        >
                          {LOG_SOURCE_COLLECTION_METHODS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="log-source-field log-source-field-endpoint">
                        <label htmlFor={`log-source-endpoint-${source.id}`}>Source URL</label>
                        <input
                          id={`log-source-endpoint-${source.id}`}
                          type="url"
                          value={source.endpoint}
                          onChange={(e) => handleSourceFieldChange(source.id, 'endpoint', e.target.value)}
                          placeholder="https://billing.example.com"
                          spellCheck={false}
                          autoComplete="off"
                        />
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-collector-${source.id}`}>Collector</label>
                        <input
                          id={`log-source-collector-${source.id}`}
                          type="text"
                          value={source.collector}
                          onChange={(e) => handleSourceFieldChange(source.id, 'collector', e.target.value)}
                          placeholder="Commray Agent"
                        />
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-index-${source.id}`}>Index</label>
                        <input
                          id={`log-source-index-${source.id}`}
                          type="text"
                          value={source.indexName}
                          onChange={(e) => handleSourceFieldChange(source.id, 'indexName', e.target.value)}
                          placeholder="payment-api-prod"
                        />
                      </div>
                      <div className="log-source-field">
                        <label htmlFor={`log-source-service-${source.id}`}>Service</label>
                        <input
                          id={`log-source-service-${source.id}`}
                          type="text"
                          value={source.serviceName}
                          onChange={(e) => handleSourceFieldChange(source.id, 'serviceName', e.target.value)}
                          placeholder="payment-service"
                        />
                      </div>
                      <div className="log-source-field log-source-field-endpoint">
                        <label htmlFor={`log-source-tags-${source.id}`}>Tags</label>
                        <textarea
                          id={`log-source-tags-${source.id}`}
                          value={source.tagsText}
                          onChange={(e) => handleSourceFieldChange(source.id, 'tagsText', e.target.value)}
                          placeholder={'team=payments\nregion=us-east4\ncloud=gcp'}
                          rows={4}
                          spellCheck={false}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSource(source.id)}
                        className="remove-source-button"
                      >
                        <X size={14} />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="log-sources-empty">No sources defined yet. Use “Add source” to configure one.</div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" onClick={handleCancelEdit} className="btn-cancel">
                Cancel
              </button>
              <button type="button" onClick={handleSaveSources} className="btn-save">
                <Check size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Search Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Save Search</h2>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Name
                </label>
                <input
                  type="text"
                  value={saveSearchName}
                  onChange={(e) => setSaveSearchName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter search name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={saveSearchDescription}
                  onChange={(e) => setSaveSearchDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter description"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSearch}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Save Search
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogExplorer;
